"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireWildcard(require("react"));
var d3 = _interopRequireWildcard(require("d3"));
var _venn = require("venn.js");
var _useChartDimensions = require("../hooks/useChartDimensions");
var _SvgChart = _interopRequireDefault(require("../components/SvgChart"));
var _useChartTooltip = require("../hooks/useChartTooltip");
var _useChartData = require("../hooks/useChartData");
var _lodash = _interopRequireDefault(require("lodash.isequal"));
var _labelFormatter = require("../utils/label-formatter");
var _color = _interopRequireDefault(require("color"));
var _useFilter = require("../hooks/useFilter");
function _getRequireWildcardCache(nodeInterop) { if (typeof WeakMap !== "function") return null; var cacheBabelInterop = new WeakMap(); var cacheNodeInterop = new WeakMap(); return (_getRequireWildcardCache = function (nodeInterop) { return nodeInterop ? cacheNodeInterop : cacheBabelInterop; })(nodeInterop); }
function _interopRequireWildcard(obj, nodeInterop) { if (!nodeInterop && obj && obj.__esModule) { return obj; } if (obj === null || typeof obj !== "object" && typeof obj !== "function") { return { default: obj }; } var cache = _getRequireWildcardCache(nodeInterop); if (cache && cache.has(obj)) { return cache.get(obj); } var newObj = {}; var hasPropertyDescriptor = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var key in obj) { if (key !== "default" && Object.prototype.hasOwnProperty.call(obj, key)) { var desc = hasPropertyDescriptor ? Object.getOwnPropertyDescriptor(obj, key) : null; if (desc && (desc.get || desc.set)) { Object.defineProperty(newObj, key, desc); } else { newObj[key] = obj[key]; } } } newObj.default = obj; if (cache) { cache.set(obj, newObj); } return newObj; }
// @ts-ignore

const BASE_MARGIN = Object.freeze({
  top: 0,
  bottom: 40,
  left: 10,
  right: 10
});
const VennDiagram = /*#__PURE__*/(0, _react.memo)(chartProps => {
  var _chart$colors;
  const {
    chart,
    animate,
    chartColors
  } = chartProps;
  const {
    yCol,
    yFormat,
    componentProps,
    labelSize,
    seriesCol
  } = chart;
  const {
    relations,
    setText,
    showIntersectionText
  } = componentProps;
  const palette = ((_chart$colors = chart.colors) === null || _chart$colors === void 0 ? void 0 : _chart$colors.schemes.categorical) || chartColors.schemes.categorical;
  const svgRef = _react.default.useRef(null);
  const boxRef = _react.default.useRef(null);
  const {
    filter,
    filterComponent
  } = (0, _useFilter.useFilter)(chartProps);
  const [animationDone, setAnimated] = (0, _react.useState)(!animate);
  let {
    width,
    height,
    margin
  } = (0, _useChartDimensions.useChartDimensions)(svgRef, BASE_MARGIN, chart.spanRow);
  const {
    data,
    seriesLabels
  } = (0, _useChartData.useChartData)(chart, filter);
  const {
    tooltipRef,
    showTooltip,
    hideTooltip
  } = (0, _useChartTooltip.useChartTooltip)(chart, data, "yCol");
  const sets = relations.map(rel => {
    const seriesData = data.find(d => d.seriesKey === rel.valueCol);
    const pointVal = seriesData && seriesData.points.at(-1) ? seriesData.points.at(-1)[yCol] : 0;
    let label = " ";
    if (setText !== "none" && (showIntersectionText || rel.sets.length === 1)) {
      if (setText === "label" || setText === "label_value") {
        label = seriesData ? chart.dataView.columns[seriesCol].categories[rel.valueCol].label : "";
        if (setText === "label_value") {
          label += " (".concat(_labelFormatter.QuantitativeFormatter[yFormat](Number(pointVal)), ")");
        }
      } else if (setText === "value") {
        label = _labelFormatter.QuantitativeFormatter[yFormat](Number(pointVal));
      }
    }
    return {
      sets: rel.sets,
      size: Number(pointVal),
      label,
      series: seriesData
    };
  });
  const whiteBackground = (0, _color.default)("white");
  function convertToHex(hexColor, opacity) {
    const colorWithOpacity = (0, _color.default)(hexColor);
    const colorOverWhite = whiteBackground.mix(colorWithOpacity, opacity);
    return colorOverWhite.hex();
  }
  let paletteIdx = 0;
  const setColor = {};
  for (const set of sets) {
    if (!set.series) {
      continue;
    }
    if (set.sets.length === 1) {
      setColor[set.series.seriesKey] = convertToHex(palette[paletteIdx++], 0.25);
    } else {
      setColor[set.series.seriesKey] = set.sets.reduce((result, curr, idx, items) => {
        const prevSet = sets.find(set => set.sets[0] === curr);
        if (!result) {
          return setColor[prevSet.series.seriesKey];
        }
        return (0, _color.default)(setColor[prevSet.series.seriesKey]).mix((0, _color.default)(result)).hex();
      }, "");
    }
  }
  ;
  const newPalette = Object.values(setColor);
  const colorPalette = d3.scaleOrdinal().domain(seriesLabels.map(s => s.key)).range(palette);
  (0, _react.useLayoutEffect)(() => {
    if (!boxRef.current) {
      return;
    }
    const venn = (0, _venn.VennDiagram)();
    venn.width(width);
    venn.height(height);
    venn.colours(sets => {
      const rel = relations.find(rel => (0, _lodash.default)(rel.sets, sets));
      return colorPalette(rel === null || rel === void 0 ? void 0 : rel.valueCol);
    });
    venn.fontSize(labelSize === "small" ? 11 : 13);
    venn.duration(0);
    try {
      d3.select(boxRef.current).datum(sets).call(venn);
      svgRef.current.removeAttribute("width");
      svgRef.current.removeAttribute("height");
      d3.select(boxRef.current).selectAll("g").on("mouseover", function (event, setData) {
        const seriesData = setData.series;
        if (!seriesData) {
          return;
        }
        let element = event.target;
        if (element.tagName === "tspan") {
          element = element.parentNode.previousSibling;
        }
        if (element) {
          element.setAttribute("data-x", seriesData.points.at(-1)[yCol]);
          element.setAttribute("data-series", seriesData.seriesKey);
          element.setAttribute("data-color", colorPalette(seriesData.seriesKey));
          showTooltip(element, "top");
        }

        // highlight the current path
        var selection = d3.select(this).transition("tooltip").duration(400);
        selection.select("path").style("stroke-width", 3).style("fill-opacity", setData.sets.length == 1 ? .4 : .1).style("stroke-opacity", 1);
      }).on("mouseout", function (event, d) {
        hideTooltip();
        var selection = d3.select(this).transition("tooltip").duration(400);
        selection.select("path").style("stroke-width", 0).style("fill-opacity", d.sets.length == 1 ? .25 : .0).style("stroke-opacity", 0);
      });
      if (!animationDone) {
        d3.select(svgRef.current).style("transform", "scale(0.5)").style("transition", "transform 0.8s");
      }
    } catch (e) {
      console.error("Error rendering Venn Diagram", e);
    }
    return () => {
      //console.log(d3.select(svgRef.current).selectAll("g").size());
      d3.select(svgRef.current).selectAll("g").remove();
    };
  }, [boxRef.current, width, height, margin, relations, labelSize, setColor, animationDone]);
  const handleVisible = (0, _react.useCallback)(() => {
    if (animationDone || !svgRef.current || !height) return;
    d3.select(svgRef.current).style("transform", "scale(1)");
  }, [animationDone, height, svgRef.current]);
  return /*#__PURE__*/_react.default.createElement(_SvgChart.default, {
    margin: margin,
    svgRef: svgRef,
    boxRef: boxRef,
    tooltipRef: tooltipRef,
    chart: chart,
    palette: newPalette,
    onVisible: handleVisible,
    filterComponent: filterComponent
  });
});
var _default = VennDiagram;
exports.default = _default;